import React, {
  Suspense,
  useState,
  useEffect,
  useContext,
} from 'react';
import { Link, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Grid,
  Toolbar,
  Typography,
  Box,
  Button,
} from '@mui/material';
import { ErrorBoundary } from '@v2/components/ui/ErrorBoundary';
import moment from 'moment-timezone';
import AddIcon from '@mui/icons-material/Add';
import AdvertiserContext from '../../AdvertiserContext';
import ManageBid from '../../ManageBid';
import EditWeighting from '../../EditWeighting';
import ArchiveUnarchive from '@components/ArchiveUnarchive';
import { useCampaigns } from '@hooks/campaigns';
import {
  LineItemStatusDisplayNames,
  DefaultLineItemStatusKeys,
  Status,
} from '@constants';
import { useWizardRedirect } from '@components/hooks/wizardRedirect';
import { editingStates } from '@components/WizardContainers';
import {
  StyledAppHeader,
  classes,
} from '@components/containers/AdGroupsIndexPage/styles';
import { AdGroupsTableContextProvider } from '@components/containers/AdGroupsIndexPage/AdGroupsTableContext';
import { AdGroupsTableV2 } from '@components/containers/AdGroupsIndexPage/AdGroupsTableV2';
import { GROUP_TYPE, useGroups } from '@apis/groups';
import { usePatchCreativeLineItem } from '@apis/creatives';
import { StatusSelect } from '@components/StatusSelect';
import { useWizardPageGroups } from '../WizardPage/hooks/useWizardPageGroups';

const AdGroupsIndexPage = props => {
  const adContext = useContext(AdvertiserContext);
  const { campaignId } = useParams();
  const { getCampaignData } = useCampaigns();

  const [campaign, setCampaign] = useState({});
  const [isEditBid, setIsEditBid] = useState(false);
  const [isEditWeighting, setIsEditWeighting] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [currentAdGroup, setCurrentAdGroup] = useState(null);
  const [archiveState, setArchiveState] = useState(null);
  const [statuses, setStatuses] = useState(DefaultLineItemStatusKeys);
  const { trigger: patchCreativeLineItem } = usePatchCreativeLineItem();

  const {
    duplicate: duplicateAdGroup,
    update: updateAdGroup,
    invalidate: invalidateGroups,
  } = useGroups(GROUP_TYPE.AD_GROUP, campaignId, {
    params: {
      force: true,
      queryProps: {
        ordering: '-active,-pending_active,-draft,archived,-start_date',
        status: statuses.join(','),
      },
    },
  });

  const { invalidate: invalidateDrags } = useGroups(
    GROUP_TYPE.STATIC_GROUP,
    campaignId,
    {
      params: {
        force: true,
      },
    }
  );

  const { newGroup, editGroup } = useWizardRedirect();

  useEffect(() => {
    invalidateGroups();
    invalidateDrags();
  }, [invalidateGroups, invalidateDrags]);

  useEffect(() => {
    if (adContext && adContext.id !== null && campaignId) {
      initCampaign(campaignId);
    }
  }, [campaignId, adContext.id]);

  useEffect(() => {
    if (hasSaved) {
      initCampaign(campaignId);
    }
  }, [hasSaved]);

  async function initCampaign(id) {
    try {
      const campaignData = await getCampaignData(id);
      setCampaign(campaignData);
    } catch (error) {
      console.log(error);
    }
  }

  function getStartDateForDupe(start) {
    // if start date is expired, set as today
    if (moment(start).isBefore()) {
      return moment(new Date()).utc().format();
    }

    // if start date is in the future, use same start date
    return start;
  }

  function getEndDateForDupe(end, campaign) {
    // if end date is expired, use campaign end date
    if (end && moment(end).isBefore()) {
      return campaign.end_date;
    }

    if (!end) {
      return null;
    }

    // if end date is in the future, use same end date
    return end;
  }

  const handleAddAdGroup = () => {
    return newGroup({ campaignId });
  };

  const handleManageAdGroup = group => {
    return editGroup({
      campaignId,
      groupId: group.id,
      query: {
        editing: editingStates.single,
      },
    });
  };

  const { groups, retargetingGroups } = useWizardPageGroups({
    campaignId,
  });

  const handleDuplicateAdGroup = async group => {
    const { name, start_date, end_date, daily_budget } = group;
    const startDate = getStartDateForDupe(start_date);
    const endDate = getEndDateForDupe(end_date, campaign);

    const result = await duplicateAdGroup(group.id, {
      remote: true,
      data: {
        name: `Copy of ${name}`,
        start_date: startDate,
        end_date: endDate,
        daily_budget,
        campaign_id: campaign.id,
        delivery_signal: undefined,
        draft: true,
        status: Status.DRAFT,
      },
    });

    groups.invalidate();
    retargetingGroups.invalidate();

    return result;
  };

  const handleIsDuplicatable = adGroup => {
    const { end_date } = adGroup;

    return (
      end_date &&
      moment(end_date).isBefore() &&
      moment(campaign.end_date).isBefore()
    );
  };

  const handleManageDisplay = display => {
    return editGroup({
      campaignId,
      groupId: `static-${display.id}`,
      query: {
        editing: editingStates.single,
      },
    });
  };

  const handleArchiveUnarchiveAdGroup = adGroup => {
    const isArchiving = adGroup.status === Status.INACTIVE;
    setArchiveState(isArchiving ? 'archiving' : 'unarchiving');
    setCurrentAdGroup(adGroup);
  };

  const handleCloseAll = () => {
    setCurrentAdGroup(null);
    invalidateGroups();
    invalidateDrags();
  };

  const handleOpenWeighting = async group => {
    setCurrentAdGroup(group);
    setIsEditWeighting(true);
  };

  const handleWeightingSubmit = async ({ weightedDelivery, creatives }) => {
    const adGroup = await updateAdGroup({
      id: currentAdGroup.id,
      creative_weighting_method: weightedDelivery ? 'WEIGHTED' : 'RANDOM',
    });

    if (adGroup.creative_weighting_method === 'WEIGHTED') {
      const requests = creatives.map(({ weighting, id }) =>
        patchCreativeLineItem({ id, weighting })
      );

      await Promise.all(requests);
    }

    handleCloseWeighting();
  };

  const handleCloseWeighting = () => {
    setIsEditWeighting(false);
    setCurrentAdGroup(null);
    handleCloseAll();
  };

  const handleOpenBid = group => {
    setCurrentAdGroup(group);
    setHasSaved(false);
    setIsEditBid(true);
  };

  const handleCloseBid = () => {
    setIsEditBid(false);
    setCurrentAdGroup(null);
    handleCloseAll();
  };

  const handleCloseArchiveUnarchive = () => {
    setArchiveState(null);
    setCurrentAdGroup(null);
    invalidateGroups();
    invalidateDrags();
  };

  return (
    <StyledAppHeader history={props.history}>
      <Box m={5} mb={2}>
        <Typography>
          <strong>
            <Link className={classes.link} to={`/campaigns/`}>
              Campaigns
            </Link>
          </strong>{' '}
          &gt; Ad Groups
        </Typography>
      </Box>

      <Box
        border={1}
        borderColor="grey.300"
        p={6}
        pt={4}
        m={4}
        borderRadius="20px"
      >
        <Toolbar disableGutters>
          <Grid container>
            <Grid
              container
              item
              alignItems="center"
              justifyContent="space-between"
              xs={12}
            >
              <Grid item xs={3}>
                <Typography variant="h6" component="div">
                  Ad Groups
                </Typography>
              </Grid>

              <Grid
                item
                container
                alignItems="center"
                justifyContent="flex-end"
                xs={9}
              >
                <Grid item>
                  <StatusSelect
                    regularStatuses={DefaultLineItemStatusKeys}
                    chosenStatuses={statuses}
                    onChange={setStatuses}
                    statusDisplayNames={LineItemStatusDisplayNames}
                  />
                </Grid>

                <Grid item>
                  {campaign && !campaign.experiment_type && (
                    <Button
                      color="primary"
                      size="small"
                      onClick={handleAddAdGroup}
                      startIcon={<AddIcon />}
                    >
                      Add Ad Group
                    </Button>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Toolbar>

        <ErrorBoundary fallback={<h2>Could not fetch ad groups.</h2>}>
          <Suspense fallback={<h2>Loading...</h2>}>
            <AdGroupsTableContextProvider
              handleManageAdGroup={handleManageAdGroup}
              handleDuplicateAdGroup={handleDuplicateAdGroup}
              handleOpenWeighting={handleOpenWeighting}
              handleOpenBid={handleOpenBid}
              handleIsDuplicatable={handleIsDuplicatable}
              handleManageDisplay={handleManageDisplay}
              handleArchiveUnarchiveAdGroup={handleArchiveUnarchiveAdGroup}
              campaignId={campaignId}
              campaign={campaign}
              statuses={statuses}
            >
              <AdGroupsTableV2 />
            </AdGroupsTableContextProvider>
          </Suspense>
        </ErrorBoundary>

        {isEditWeighting && (
          <EditWeighting
            data={{
              weightedDelivery:
                currentAdGroup.creative_weighting_method === 'WEIGHTED',
            }}
            adGroup={currentAdGroup}
            onSubmit={handleWeightingSubmit}
            onClose={handleCloseWeighting}
            isOpen={isEditWeighting}
          />
        )}

        {isEditBid && (
          <ManageBid
            adGroup={currentAdGroup}
            isOpen={isEditBid}
            onClose={handleCloseBid}
            setHasSaved={setHasSaved}
            campaignId={campaignId}
          />
        )}

        {archiveState && (
          <ArchiveUnarchive
            isOpen={!!archiveState}
            item={currentAdGroup}
            onClose={handleCloseArchiveUnarchive}
            isArchiving={archiveState === 'archiving'}
            itemType="Ad Group"
          />
        )}
      </Box>
    </StyledAppHeader>
  );
};

AdGroupsIndexPage.propTypes = {
  history: PropTypes.object,
};

export default AdGroupsIndexPage;
